import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Card from '@mui/material/Card';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import TableSortLabel from '@mui/material/TableSortLabel';
import TablePagination from '@mui/material/TablePagination';
import { styled } from '@mui/material/styles';
import { toast } from 'react-toastify';
import api from '../services/api';
import Chip from '@mui/material/Chip';
import { Search, Eye, Users as ClusterLogoIcon } from 'lucide-react';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import { useNavigate } from 'react-router-dom';

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
}));

export default function Cluster() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('id');
  const [filterName, setFilterName] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchData = async () => {
    try {
      const res = await api.get('/cluster');
      setList(res.data);
    } catch (error) {
      console.error('Error fetching cluster list:', error);
      toast.error('Errore nel caricamento dei dati Cluster');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredList = list
    .filter((item) => {
      if (!filterName) return true;
      const search = filterName.toLowerCase();
      return (
        item.label.toLowerCase().includes(search) ||
        item.cluster_id.toString().includes(search) ||
        item.id.toString().includes(search)
      );
    })
    .sort((a, b) => {
      const isAsc = order === 'asc';
      if (orderBy === 'id' || orderBy === 'cluster_id' || orderBy === 'total_customers') {
        const valA = a[orderBy] || 0;
        const valB = b[orderBy] || 0;
        return isAsc ? valA - valB : valB - valA;
      }
      const valA = a[orderBy] ? a[orderBy].toString().toLowerCase() : '';
      const valB = b[orderBy] ? b[orderBy].toString().toLowerCase() : '';
      if (valA < valB) return isAsc ? -1 : 1;
      if (valA > valB) return isAsc ? 1 : -1;
      return 0;
    });

  const totalCustomers = filteredList.reduce((sum, it) => sum + (it.total_customers || 0), 0);

  const goToDetail = (item) => {
    navigate(`/cluster/${item.id}`);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <ClusterLogoIcon size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
              Cluster (Remoto)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Segmenti di clientela calcolati su base dati esterna
            </Typography>
          </Box>
        </Box>
      </Box>

      <StyledCard>
        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            size="small"
            value={filterName}
            onChange={handleFilterByName}
            placeholder="Cerca per label, cluster id..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={20} />
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: 300 }}
          />
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.neutral' }}>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'id'}
                    direction={orderBy === 'id' ? order : 'asc'}
                    onClick={() => handleRequestSort('id')}
                  >
                    ID
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'cluster_id'}
                    direction={orderBy === 'cluster_id' ? order : 'asc'}
                    onClick={() => handleRequestSort('cluster_id')}
                  >
                    Cluster ID
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'label'}
                    direction={orderBy === 'label' ? order : 'asc'}
                    onClick={() => handleRequestSort('label')}
                  >
                    Label
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === 'total_customers'}
                    direction={orderBy === 'total_customers' ? order : 'asc'}
                    onClick={() => handleRequestSort('total_customers')}
                  >
                    Totale Clienti
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  % sul totale
                </TableCell>
                <TableCell align="center">
                  Azioni
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    Caricamento...
                  </TableCell>
                </TableRow>
              ) : filteredList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <Typography variant="body1" color="text.secondary">
                      Nessun dato trovato
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredList
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>{item.id}</TableCell>
                    <TableCell>
                      <Chip label={item.cluster_id} size="small" color="primary" variant="outlined" />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{item.label}</TableCell>
                    <TableCell align="right">
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 28,
                          height: 28,
                          borderRadius: 0,
                          bgcolor: 'rgba(255, 171, 0, 0.08)',
                          color: '#FF9800',
                          boxShadow: '0 0 0 1px rgba(255, 171, 0, 0.24)',
                          fontWeight: 'bold',
                          fontSize: '0.8rem',
                        }}
                      >
                        {item.total_customers || 0}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      {totalCustomers > 0 ? (
                        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                          <CircularProgress
                            variant="determinate"
                            value={100}
                            size={36}
                            thickness={6}
                            sx={{ color: 'rgba(145, 158, 171, 0.24)' }}
                          />
                          <CircularProgress
                            variant="determinate"
                            value={((item.total_customers || 0) * 100) / totalCustomers}
                            size={36}
                            thickness={6}
                            sx={{ position: 'absolute', left: 0 }}
                            color="warning"
                          />
                          <Box
                            sx={{
                              top: 0,
                              left: 0,
                              bottom: 0,
                              right: 0,
                              position: 'absolute',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.primary', fontSize: '0.7rem' }}>
                              {(((item.total_customers || 0) * 100) / totalCustomers).toFixed(2)}%
                            </Typography>
                          </Box>
                        </Box>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton color="primary" onClick={() => goToDetail(item)}>
                        <Eye size={18} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filteredList.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
        />
      </StyledCard>
    </Box>
  );
}
